import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useCart } from "../context/CartContext";
import {
  fetchMerchantCategories,
  fetchMerchantCategoryProducts,
  fetchMerchantProducts,
  fetchStoreMerchant,
} from "../utils/api";

const API_IMAGE = process.env.REACT_APP_IMAGE_URL || "";
const resolveImage = (value, merchant) => {
  if (!value) return null;
  if (/^https?:\/\//i.test(value)) return value;

  const endpoint = merchant?.endpoints?.products;
  if (endpoint) {
    try {
      const origin = new URL(endpoint).origin;
      const path = String(value).replace(/^\//, "");
      return `${origin}/${path.startsWith("storage/") ? path : `storage/${path}`}`;
    } catch (_) {
      // Fall back to the configured image host below.
    }
  }

  return `${API_IMAGE}${value.startsWith("/") ? "" : "/"}${value}`;
};
const money = (value) =>
  new Intl.NumberFormat("en-SL", {
    style: "currency",
    currency: "SLE",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

function MerchantStore() {
  const { subdomain } = useParams();
  const { addToCart, cart } = useCart();
  const [merchant, setMerchant] = useState(null);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [active, setActive] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [productsLoading, setProductsLoading] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    setLoading(true);
    fetchStoreMerchant(subdomain)
      .then(async (merchantResponse) => {
        const selectedMerchant = merchantResponse?.data;
        setMerchant(selectedMerchant || null);

        const [categoryResponse, productResponse] = await Promise.all([
          fetchMerchantCategories(selectedMerchant),
          fetchMerchantProducts(selectedMerchant),
        ]);

        setCategories(
          Array.isArray(categoryResponse?.data)
            ? categoryResponse.data.filter(
                (category) =>
                  category.show_on_website === true ||
                  Number(category.show_on_website) === 1,
              )
            : [],
        );
        setProducts(
          Array.isArray(productResponse?.data)
            ? productResponse.data.flat(Infinity).filter(Boolean)
            : [],
        );
      })
      .catch((error) =>
        setMessage(
          error.response?.data?.message ||
            "This restaurant menu is temporarily unavailable.",
        ),
      )
      .finally(() => setLoading(false));
  }, [subdomain]);

  const visibleProducts = useMemo(
    () =>
      products.filter((product) => {
        const term = search.trim().toLowerCase();
        return (
          !term ||
          String(product.name || "")
            .toLowerCase()
            .includes(term)
        );
      }),
    [products, search],
  );

  const selectCategory = async (categoryId) => {
    if (String(categoryId) === String(active)) return;
    setActive(categoryId);
    setProductsLoading(true);
    setMessage("");

    try {
      const response =
        categoryId === "all"
          ? await fetchMerchantProducts(merchant)
          : await fetchMerchantCategoryProducts(merchant, categoryId);
      setProducts(
        Array.isArray(response?.data)
          ? response.data.flat(Infinity).filter(Boolean)
          : [],
      );
    } catch (error) {
      setProducts([]);
      setMessage(
        error.response?.data?.message ||
          "Products could not be loaded for this category.",
      );
    } finally {
      setProductsLoading(false);
    }
  };

  const add = (product) => {
    const hasOtherMerchant = cart.some(
      (item) =>
        item.merchant_id && String(item.merchant_id) !== String(merchant.id),
    );
    if (hasOtherMerchant) {
      setMessage(
        "Your basket already contains items from another restaurant. Complete or clear that basket first.",
      );
      return;
    }
    const result = addToCart({
      ...product,
      id: `${merchant.id}:${product.id}`,
      source_product_id: product.id,
      cart_image: resolveImage(
        product.imageViews
          ? Object.values(product.imageViews)[0]
          : product.image ||
              product.image_url ||
              product.photo ||
              product.product_image,
        merchant,
      ),
      quantity: 1,
      price: Number(product.price || 0),
      merchant_id: merchant.id,
      merchant_subdomain: merchant.subdomain,
      merchant_auth_token:
        merchant.auth_token || merchant.authentication_token || "",
      merchant_name: merchant.business_name,
      merchant_phone: merchant.phone_number || "",
      merchant_email: merchant.business_email || "",
      selectedSize: "",
      selectedColor: "",
      format: "",
    });

    if (result?.success === false) {
      setMessage(result.message);
      return;
    }

    setMessage(`${product.name} added to your basket.`);
    window.setTimeout(() => setMessage(""), 2500);
  };

  if (loading)
    return (
      <div className="min-h-screen bg-[#fbfaf7] px-4 py-12">
        <div className="mx-auto h-80 max-w-7xl animate-pulse rounded-[2.5rem] bg-gray-200" />
        <div className="mx-auto mt-10 grid max-w-7xl gap-6 md:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div
              key={i}
              className="h-80 animate-pulse rounded-3xl bg-gray-100"
            />
          ))}
        </div>
      </div>
    );
  if (!merchant)
    return (
      <div className="min-h-[70vh] bg-[#fbfaf7] px-4 py-24 text-center">
        <h1 className="font-playfair-display text-4xl font-bold">
          Store unavailable
        </h1>
        <p className="mt-4 text-gray-500">{message}</p>
        <Link
          to="/restaurants"
          className="mt-8 inline-block font-bold text-[#6f342e]"
        >
          Browse restaurants →
        </Link>
      </div>
    );

  const banner = resolveImage(
    merchant.store_banner || merchant.business_logo,
    merchant,
  );
  const isMarketing = merchant.service_type === "marketing";
  const phoneNumber = merchant.phone_number || "";
  const businessEmail = merchant.business_email || "";

  return (
    <div className="min-h-screen bg-[#fbfaf7] pb-24">
      <section className="px-3 pt-3 sm:px-6 sm:pt-6 lg:px-8">
        <div className="relative mx-auto min-h-[310px] max-w-7xl overflow-hidden rounded-[1.75rem] bg-gradient-to-br from-[#2b1c19] via-[#6c382f] to-[#d39460] shadow-[0_24px_60px_rgba(44,28,24,.18)] sm:min-h-[410px] sm:rounded-[2.5rem]">
          {banner && (
            <img
              src={banner}
              alt=""
              className="absolute inset-0 h-full w-full object-cover"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/45 to-black/10" />
          <div className="relative flex min-h-[310px] max-w-3xl flex-col justify-end p-5 text-white sm:min-h-[410px] sm:p-12">
            <Link
              to="/restaurants"
              className="mb-auto w-fit rounded-full border border-white/20 bg-black/15 px-4 py-2 text-xs font-bold backdrop-blur"
            >
              ← All restaurants
            </Link>
            <p className="text-xs font-bold uppercase tracking-[.22em] text-[#f3c89e]">
              {isMarketing ? "Merchant catalogue" : "Live restaurant menu"}
            </p>
            <h1 className="mt-2 font-playfair-display text-3xl font-bold sm:mt-3 sm:text-6xl">
              {merchant.business_name}
            </h1>
            <p className="mt-3 line-clamp-2 max-w-2xl text-sm leading-6 text-white/75 sm:mt-4 sm:block sm:text-base sm:leading-7">
              {merchant.store_description ||
                (isMarketing
                  ? "Explore this merchant’s products and contact the business directly."
                  : "Browse the kitchen’s available categories and freshly prepared menu.")}
            </p>
            <div className="mt-4 flex flex-wrap gap-2 text-[11px] font-semibold sm:mt-6 sm:text-xs">
              <span className="rounded-full bg-white/10 px-4 py-2 backdrop-blur">
                {merchant.business_address || "Available online"}
              </span>
              <span className="rounded-full bg-white/10 px-4 py-2 backdrop-blur">
                {isMarketing ? "Direct enquiry" : "Live availability"}
              </span>
            </div>
            {isMarketing && (
              <div className="mt-5 flex flex-wrap gap-3">
                {phoneNumber && (
                  <a
                    href={`tel:${phoneNumber}`}
                    className="rounded-full bg-white px-5 py-3 text-sm font-bold text-[#542f29] transition hover:bg-[#f6e8dc]"
                  >
                    Call {phoneNumber}
                  </a>
                )}
                {businessEmail && (
                  <a
                    href={`mailto:${businessEmail}`}
                    className="rounded-full border border-white/35 bg-white/10 px-5 py-3 text-sm font-bold text-white backdrop-blur transition hover:bg-white/20"
                  >
                    Email merchant
                  </a>
                )}
                {!phoneNumber && !businessEmail && (
                  <span className="rounded-full border border-white/25 bg-white/10 px-5 py-3 text-sm text-white/75">
                    Contact details unavailable
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="sticky top-[64px] z-30 mt-4 border-y border-black/5 bg-[#fbfaf7]/95 px-3 py-3 backdrop-blur-xl sm:mt-7 sm:px-6 sm:py-4 lg:top-20 lg:px-8">
        <div className="mx-auto flex max-w-7xl flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="mobile-scroll-row flex gap-2 overflow-x-auto pb-1">
            {[{ id: "all", name: "All dishes" }, ...categories].map(
              (category) => (
                <button
                  key={category.id}
                  onClick={() => selectCategory(category.id)}
                  disabled={productsLoading}
                  className={`whitespace-nowrap rounded-full px-4 py-2.5 text-xs font-bold transition disabled:opacity-60 sm:px-5 sm:text-sm ${String(active) === String(category.id) ? "bg-[#4b2b26] text-white shadow-md" : "border border-black/10 bg-white text-gray-600 hover:border-[#4b2b26]"}`}
                >
                  {category.name}
                </button>
              ),
            )}
          </div>
          <div className="relative shrink-0">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search this menu"
              className="w-full rounded-full border border-black/10 bg-white py-3 pl-11 pr-5 text-sm outline-none focus:border-[#6f342e] lg:w-72"
            />
            <span className="absolute left-4 top-1/2 -translate-y-1/2">⌕</span>
          </div>
        </div>
      </section>

      <section className="px-3 pt-7 sm:px-6 sm:pt-12 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="flex items-end justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[.2em] text-[#8a4b3e]">
                {active === "all"
                  ? isMarketing
                    ? "Complete catalogue"
                    : "Complete menu"
                  : categories.find((c) => String(c.id) === String(active))
                      ?.name}
              </p>
              <h2 className="mt-1 font-playfair-display text-2xl font-bold sm:mt-2 sm:text-4xl">
                {isMarketing
                  ? "Discover what they offer."
                  : "Choose something memorable."}
              </h2>
            </div>
            <span className="hidden text-sm text-gray-500 sm:block">
              {visibleProducts.length} items
            </span>
          </div>
          {message && (
            <div className="mt-6 rounded-2xl border border-[#dcb9a9] bg-[#fff6ef] px-5 py-4 text-sm font-semibold text-[#6f342e]">
              {message}
            </div>
          )}
          {productsLoading ? (
            <div className="mt-6 grid grid-cols-2 gap-3 sm:mt-9 sm:gap-6 lg:grid-cols-3">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-80 animate-pulse rounded-[1.75rem] bg-gray-100"
                />
              ))}
            </div>
          ) : visibleProducts.length ? (
            <div className="mt-9 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {visibleProducts.map((product, index) => {
                const image = resolveImage(
                  product.imageViews
                    ? Object.values(product.imageViews)[0]
                    : product.image ||
                        product.image_url ||
                        product.photo ||
                        product.product_image,
                  merchant,
                );
                const categoryName =
                  product.category?.name ||
                  categories.find(
                    (c) => String(c.id) === String(product.category_id),
                  )?.name ||
                  categories.find((c) => String(c.id) === String(active))?.name;
                const unavailable =
                  Number(product.out_of_stock) === 1 ||
                  (Number(product.has_stock) === 1 &&
                    Number(product.stock) <= 0);
                return (
                  <article
                    key={`${product.id}-${index}`}
                  className="tap-card group overflow-hidden rounded-2xl border border-black/5 bg-white shadow-[0_10px_30px_rgba(44,31,26,.07)] transition duration-300 sm:rounded-[1.75rem] sm:duration-500 sm:hover:-translate-y-1 sm:hover:shadow-[0_22px_60px_rgba(44,31,26,.12)]"
                  >
                    <div className="relative flex h-36 items-center justify-center overflow-hidden bg-gradient-to-br from-[#f5e9dc] to-[#e6c5a6] sm:h-52">
                      {image ? (
                        <img
                          src={image}
                          alt={product.name}
                          className="h-full w-full object-cover transition duration-700 group-hover:scale-105"
                        />
                      ) : (
                        <span className="text-6xl">🍲</span>
                      )}
                      {!isMarketing && unavailable && (
                        <div className="absolute inset-0 flex items-center justify-center bg-white/75 text-xs font-bold uppercase tracking-[.18em] text-gray-600 backdrop-blur-sm">
                          Unavailable
                        </div>
                      )}
                    </div>
                    <div className="p-3 sm:p-6">
                      <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                        <div>
                          <p className="text-[11px] font-bold uppercase tracking-[.16em] text-[#9b6254]">
                            {categoryName}
                          </p>
                          <h3 className="mt-1 line-clamp-2 font-playfair-display text-base font-bold leading-tight text-[#211915] sm:mt-2 sm:text-2xl">
                            {product.name}
                          </h3>
                        </div>
                        <strong className="whitespace-nowrap text-xs text-[#5f342d] sm:text-base">
                          {money(product.price)}
                        </strong>
                      </div>
                      <p className="mt-3 hidden line-clamp-2 min-h-[3rem] text-sm leading-6 text-gray-500 sm:block">
                        {product.description ||
                          product.code ||
                          (isMarketing
                            ? "Contact this merchant for more information."
                            : "Prepared by this kitchen and available to order.")}
                      </p>
                      {isMarketing ? (
                        <div className="mt-5 grid gap-2 sm:grid-cols-2">
                          {phoneNumber && (
                            <a
                              href={`tel:${phoneNumber}`}
                              className="rounded-full bg-[#35231f] px-4 py-3 text-center text-sm font-bold text-white transition hover:bg-[#6f342e]"
                            >
                              Call merchant
                            </a>
                          )}
                          {businessEmail && (
                            <a
                              href={`mailto:${businessEmail}?subject=${encodeURIComponent(`Enquiry about ${product.name}`)}`}
                              className="rounded-full border border-[#6f342e] px-4 py-3 text-center text-sm font-bold text-[#6f342e] transition hover:bg-[#fff6ef]"
                            >
                              Send email
                            </a>
                          )}
                          {!phoneNumber && !businessEmail && (
                            <span className="col-span-full rounded-full bg-gray-100 px-4 py-3 text-center text-sm font-semibold text-gray-500">
                              Contact details unavailable
                            </span>
                          )}
                        </div>
                      ) : (
                        <button
                          onClick={() => add(product)}
                          disabled={unavailable}
                          className="mt-3 w-full rounded-full bg-[#35231f] px-3 py-3 text-xs font-bold text-white transition active:scale-[.98] hover:bg-[#6f342e] disabled:cursor-not-allowed disabled:bg-gray-300 sm:mt-5 sm:px-5 sm:py-3.5 sm:text-sm"
                        >
                          {unavailable ? "Unavailable" : "Add to basket +"}
                        </button>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="mt-10 rounded-3xl border border-dashed border-gray-300 px-6 py-20 text-center text-gray-500">
              No products are available in this selection.
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export default MerchantStore;
